import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, ChevronRight, Layers, Lock, Settings, Sparkles, Wand2 } from "lucide-react";
import useBusinessProfile from "../hooks/useBusinessProfile";
import ConnectTallyBanner from "../components/ConnectTallyBanner";
import { BUSINESS_TYPES, MODIFIER_FLAGS, isPopulatedType } from "../lib/businessTypeConfig";
import {
  activePopulatedTypes, activeStubTypes,
  semanticFieldForPanel, derivePanelValue,
} from "../lib/resolveBusinessPanels";
import { hasSemanticData, suggestedConnectorFor } from "../lib/sourceConnectors";
import { API_BASE } from "../lib/backendUrl";

// v1.5.2 (Feb 2026, Benchmarking Phase 1): the panel.source → semantic-field
// map and the per-panel real-value derivation moved to
// `lib/resolveBusinessPanels.js` (`PANEL_SOURCE_TO_SEMANTIC` /
// `derivePanelValue`) so Benchmarking's domain-adaptive KPI strip reuses the
// exact same live/awaiting-source logic instead of a second copy.
const semanticFieldFor = semanticFieldForPanel;
const derivedValueForPanel = derivePanelValue;

// ---------------------------------------------------------------------------
// v1.4.5 — map the customer's signup-time `industry` answer (free text /
// dropdown chosen at /business-profile onboarding) to the cockpit's
// `primary_business_model` enum. Best-effort substring match; returns
// `null` if the industry doesn't cleanly map (then the nudge button
// stays hidden and the user picks manually in Admin → Setup Checklist).
// ---------------------------------------------------------------------------
function mapIndustryToBusinessModel(industry) {
  if (!industry) return null;
  const s = String(industry).toLowerCase().trim();
  if (!s) return null;
  // Order matters — most-specific keywords first so e.g. "electronics
  // manufacturing" maps to "ems" rather than the broader "manufacturing".
  if (s.includes("electronic") || s.includes("ems") || s.includes("pcb"))      return "ems";
  if (s.includes("ecommerce") || s.includes("e-commerce") || s.includes("d2c")
      || s.includes("direct-to-consumer") || s.includes("direct to consumer")) return "d2c";
  if (s.includes("manufactur"))                                                 return "manufacturing";
  if (s.includes("trad") || s.includes("distrib") || s.includes("wholesal"))    return "trading";
  if (s.includes("servic") || s.includes("consult") || s.includes("agency"))    return "services";
  return null;
}

// ---------------------------------------------------------------------------
// Single panel card — shows label + "live" pill OR "Connect [source]" empty
// state, never any made-up numbers. The engine intentionally renders the
// tile so the customer can see what they would unlock by connecting; if
// the data is live we surface the headline metric inline.
// ---------------------------------------------------------------------------
const TIER_TONE = {
  core:      { bar: "bg-blue-500",    chip: "bg-blue-50 text-blue-700",      label: "Core" },
  primary:   { bar: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700",label: "Primary" },
  secondary: { bar: "bg-indigo-500",  chip: "bg-indigo-50 text-indigo-700",  label: "Additional" },
  modifier:  { bar: "bg-amber-500",   chip: "bg-amber-50 text-amber-700",    label: "Modifier" },
  strategic: { bar: "bg-fuchsia-500", chip: "bg-fuchsia-50 text-fuchsia-700",label: "Strategic" },
};

function PanelTile({ panel, customerData, tier }) {
  const semField  = semanticFieldFor(panel);
  const isLive    = semField ? hasSemanticData(customerData, semField) : false;
  const hint      = semField ? suggestedConnectorFor(semField) : null;
  const tone      = TIER_TONE[tier] || TIER_TONE.core;
  const liveValue = isLive ? derivedValueForPanel(panel, customerData) : null;

  return (
    <div
      data-testid={`bp-panel-${panel.id}`}
      className="relative overflow-hidden rounded-lg border border-slate-200 bg-white p-4 shadow-card"
    >
      <div className={`absolute inset-x-0 top-0 h-1 ${tone.bar}`} />
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
            {tone.label}
          </div>
          <div className="mt-1 text-[13.5px] font-bold text-[#0F1B2D]">{panel.label}</div>
          <div className="mt-1 text-[11.5px] text-slate-500">{panel.copy}</div>
        </div>
        {isLive ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Live
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10.5px] font-semibold text-slate-600">
            <Lock size={10} /> Awaiting source
          </span>
        )}
      </div>

      {isLive && liveValue && (
        <div data-testid={`bp-panel-${panel.id}-value`} className="mt-3 text-[18px] font-bold tabular-nums text-[#0F1B2D]">
          {liveValue}
        </div>
      )}

      {!isLive && (
        <div className="mt-3 rounded-md border border-dashed border-slate-300 bg-slate-50/60 px-3 py-2 text-[11.5px] text-slate-600">
          {hint
            ? (
              <>
                Populated by <span className="font-semibold text-slate-700">{hint.connectors[0]}</span>
                {hint.connectors.length > 1 && <> · later: {hint.connectors.slice(1).join(", ")}</>}
                <Link to="/integrations" className="ml-2 inline-flex items-center gap-0.5 font-semibold text-emerald-700 hover:text-emerald-800">
                  Connect <ChevronRight size={11} />
                </Link>
              </>
            )
            : <>Source: <span className="font-semibold text-slate-700">{panel.source || "—"}</span>. Populated once captured in Setup Checklist or a connector ships.</>
          }
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tier section — collapsible group of panels under a tier header.
// ---------------------------------------------------------------------------
function TierSection({ title, subtitle, panels, customerData, tier, testid }) {
  if (!panels.length) return null;
  return (
    <section data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-5 shadow-card">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-[14px] font-bold text-[#1E3A5F]">{title}</div>
          {subtitle && <div className="text-[12px] text-slate-500">{subtitle}</div>}
        </div>
        <div className="text-[11px] font-semibold text-slate-500">{panels.length} module{panels.length === 1 ? "" : "s"}</div>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {panels.map((p) => (
          <PanelTile key={`${tier}-${p.id}`} panel={p} customerData={customerData} tier={tier} />
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Active-profile chip strip — shows what's currently selected & links to admin
// ---------------------------------------------------------------------------
function ActiveProfileStrip({ profile }) {
  const primary = profile.primary_business_model;
  const primaryLabel = primary ? BUSINESS_TYPES[primary]?.label : null;
  const additional   = (profile.additional_models || []).map((id) => BUSINESS_TYPES[id]?.label).filter(Boolean);
  const flagsOn      = Object.entries(profile.modifier_flags || {})
                              .filter(([, v]) => !!v)
                              .map(([k]) => MODIFIER_FLAGS[k]?.label || k);

  return (
    <div data-testid="bp-profile-strip" className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-card">
      <Briefcase size={14} className="text-emerald-600" />
      <span className="text-[12px] font-semibold text-[#1E3A5F]">Business profile:</span>
      {primaryLabel
        ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">{primaryLabel}{primary && !isPopulatedType(primary) ? " · stub" : ""}</span>
        : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">No primary type set</span>}
      {additional.map((l, i) => (
        <span key={`bp-strip-add-${i}`} className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700">+{l}</span>
      ))}
      {flagsOn.map((l, i) => (
        <span key={`bp-strip-flag-${i}`} className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">{l}</span>
      ))}
      <span className="ml-auto text-[11px] text-slate-500">
        Edit in Admin → Setup Checklist → Business Profile
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------
export default function BusinessPanels() {
  const { profile, panels, tiers, hasProfile, customerData, dataAvailable, isLoading, companyName } = useBusinessProfile();
  // v1.4.5 — pre-fill suggestion from `customers.industry` (collected
  // at signup). Avoids the trap where a user picked "Manufacturing" on
  // the onboarding form, then never saved a primary_business_model in
  // Admin → Setup Checklist, leaving the cockpit reading "No primary
  // type set" forever (observed on Turing Eastland, 2026-06-24).
  const [nudgeBusy, setNudgeBusy] = useState(false);
  const [nudgeErr, setNudgeErr]   = useState("");
  const suggestedModel = !hasProfile && customerData?.customer_industry
    ? mapIndustryToBusinessModel(customerData.customer_industry)
    : null;

  const acceptSuggestion = async () => {
    if (!suggestedModel || nudgeBusy) return;
    setNudgeBusy(true); setNudgeErr("");
    try {
      const res = await fetch(`${API_BASE}/customer/set-primary-business-model`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer_id:            customerData?.customer_id,
          primary_business_model: suggestedModel,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Re-fetch dashboard payload so the cockpit re-renders with the
      // newly-set primary model.
      window.location.reload();
    } catch (e) {
      setNudgeErr("Could not save — please try again or use Admin → Setup Checklist.");
      setNudgeBusy(false);
    }
  };

  const core      = panels.filter((p) => p.tier === "core");
  const primary   = panels.filter((p) => p.tier === "primary");
  const secondary = panels.filter((p) => p.tier === "secondary");
  const modifier  = panels.filter((p) => p.tier === "modifier");
  const strategic = panels.filter((p) => p.tier === "strategic");

  // Group secondary by originSecondaryType so each additional model is its
  // own collapsible group with the type's label.
  const secondaryByType = secondary.reduce((acc, p) => {
    const key = p.originSecondaryType || p.originType || "additional";
    (acc[key] = acc[key] || []).push(p);
    return acc;
  }, {});
  const secondaryGroups = Object.entries(secondaryByType);

  const populatedTypes = activePopulatedTypes(profile);
  const stubTypes      = activeStubTypes(profile);

  const primaryLabel = profile.primary_business_model
    ? BUSINESS_TYPES[profile.primary_business_model]?.label
    : "Universal";

  return (
    <div data-testid="business-panels-module">
      <ConnectTallyBanner dataAvailable={dataAvailable} isLoading={isLoading} />
      <div className="space-y-5 px-6 py-5">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Business Adaptivity</div>
            <h2 className="text-[20px] font-bold text-[#1E3A5F]">
              {companyName ? `${companyName} — ` : ""}{primaryLabel} cockpit
            </h2>
          </div>
          <div className="hidden md:flex items-center gap-2 text-[12px] text-slate-500">
            <Layers size={13} className="text-emerald-600" />
            {panels.length} modules unlocked
          </div>
        </div>

        <ActiveProfileStrip profile={profile} />

        {!hasProfile && (
          <div data-testid="bp-no-profile" className="rounded-xl border border-dashed border-amber-300 bg-amber-50/60 px-4 py-6 text-[12.5px] text-amber-800">
            <div className="flex items-start gap-2">
              <Settings size={14} className="mt-0.5 text-amber-600" />
              <div className="flex-1">
                <div className="font-semibold">No primary business type selected.</div>
                <div className="mt-1 text-amber-700">
                  The dashboard is rendering only universal-core financial KPIs.
                  Open Admin → Setup Checklist → Business Profile and pick the
                  primary model (Manufacturing / Trading / D2C / EMS) so the
                  domain-specific modules light up.
                </div>

                {/* v1.4.5 — one-click nudge: prefill from signup industry */}
                {suggestedModel && BUSINESS_TYPES[suggestedModel] && (
                  <div data-testid="bp-prefill-nudge" className="mt-3 flex flex-wrap items-center gap-2 rounded-md bg-white px-3 py-2 ring-1 ring-amber-200">
                    <Wand2 size={13} className="text-emerald-600" />
                    <span className="text-[12px] text-slate-700">
                      You picked <span className="font-semibold">{customerData?.customer_industry}</span> at signup —
                      set your primary model to <span className="font-semibold">{BUSINESS_TYPES[suggestedModel].label}</span>?
                    </span>
                    <button
                      type="button"
                      data-testid="bp-prefill-accept"
                      disabled={nudgeBusy}
                      onClick={acceptSuggestion}
                      className="ml-auto rounded-md bg-emerald-600 px-3 py-1 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                    >
                      {nudgeBusy ? "Saving…" : `Set as ${BUSINESS_TYPES[suggestedModel].label}`}
                    </button>
                    {nudgeErr && <span className="basis-full text-[11px] text-red-600">{nudgeErr}</span>}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Stub-type banner (services / saas / retail / healthcare) */}
        {stubTypes.length > 0 && (
          <div data-testid="bp-stub-banner" className="rounded-xl border border-dashed border-blue-300 bg-blue-50/60 px-4 py-4 text-[12.5px] text-blue-800">
            <div className="flex items-start gap-2">
              <Sparkles size={14} className="mt-0.5 text-blue-600" />
              <div>
                <span className="font-semibold">Module pack coming soon</span>
                <span className="ml-1">for {stubTypes.map((t) => BUSINESS_TYPES[t.id]?.label).join(", ")}.</span>
                <div className="mt-1 text-blue-700">
                  Universal-core financials and any matching modifier panels
                  are already rendering below. Domain-specific cards (e.g.
                  utilisation, MRR/ARR, sales-per-sq-ft) will ship in the
                  next phase.
                </div>
              </div>
            </div>
          </div>
        )}

        <TierSection
          testid="bp-section-primary"
          title={primary.length > 0 ? `${primaryLabel} core` : "Primary domain"}
          subtitle={primary.length > 0
            ? "The headline modules for this business type"
            : "Set a primary business model in Admin → Setup Checklist to populate this section"}
          panels={primary}
          customerData={customerData}
          tier="primary"
        />

        {secondaryGroups.map(([typeId, list]) => (
          <TierSection
            key={`secondary-${typeId}`}
            testid={`bp-section-secondary-${typeId}`}
            title={`${BUSINESS_TYPES[typeId]?.label || typeId} (additional)`}
            subtitle="Collapsible secondary modules from your additional business model"
            panels={list}
            customerData={customerData}
            tier="secondary"
          />
        ))}

        <TierSection
          testid="bp-section-modifier"
          title="Modifier-driven panels"
          subtitle={modifier.length > 0
            ? "Unlocked by your business profile flags (imports / exports / B2B / B2C / retail outlets)"
            : "Turn on a modifier flag (imports, exports, B2B, B2C, retail) in admin to add panels here"}
          panels={modifier}
          customerData={customerData}
          tier="modifier"
        />

        <TierSection
          testid="bp-section-core"
          title="Universal core financials"
          subtitle="These KPIs render for every business — straight from your Tally + GST data"
          panels={core}
          customerData={customerData}
          tier="core"
        />

        <TierSection
          testid="bp-section-strategic"
          title="Strategic / new-age layer"
          subtitle="Cross-domain views — CCC trend, AI receivables-at-risk, scenario planner, forex exposure"
          panels={strategic}
          customerData={customerData}
          tier="strategic"
        />

        <div className="text-center text-[11px] text-slate-500">
          {populatedTypes.length} populated type{populatedTypes.length === 1 ? "" : "s"} · {stubTypes.length} stub · {tiers.core} core · {tiers.primary} primary · {tiers.secondary} secondary · {tiers.modifier} modifier · {tiers.strategic} strategic
        </div>
      </div>
    </div>
  );
}
